import {Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {Permission, PermissionAssignment, PermissionObjectType, PermissionSubjectType} from 'smag-commons/dto/api';
import {Hierarchy, HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {AuthorizedObject} from '../../../api/dto';
import {combineLatest, Observable, of, Subject} from 'rxjs';
import {first, map, shareReplay, switchMap, takeUntil, tap} from 'rxjs/operators';
import {ALL_RESOURCE_PERMISSION_PATTERNS, allResourcePermissionPatterns, can} from '../../../shared/permissions';
import {PermissionsService} from '../../../shared/permissions.service';
import {MatDialog} from '@angular/material/dialog';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {I18NService} from '../../../i18n/i18n.service';
import {PermissionAssignmentObject} from '../new-permission-assignment/new-permission-assignment.component';
import {EntityNicePathPipe} from '../../../shared/shared.pipe';
import {isDefined} from '../../../commons/commons';
import {AssignmentSubject} from '../permission-assignments/permission-assignments.component';
import {EntityType, RootNode} from 'smag-commons/cfg/hierarchy';
import {SmagApiService} from '../../../api/smag-api.service';
import {
    PatternPermissionSettings,
    PermissionMode,
    PermissionSettings
} from '../permission-list/permission-list.component';
import {AbstractControl, FormControl, ValidationErrors, Validators} from '@angular/forms';
import {
    ENTITY_NAME_FORBIDDEN_CHARACTERS,
    ENTITY_NAME_MAX_LENGTH,
    ENTITY_NAME_VALIDATION_PATTERN,
    entityNameError
} from '../../../shared/entity-names';
import {validateNotBlank} from '../../../commons/validators';

const NEW_ROOT_NODE_DIALOG_MIN_WIDTH = 500;

@Component({
    selector: 'app-perm-admin-resources',
    templateUrl: './perm-admin-resources.component.html',
    styleUrls: ['./perm-admin-resources.component.scss']
})
export class PermAdminResourcesComponent implements OnInit, OnDestroy {

    private destroyed = new Subject<void>();
    private npp: EntityNicePathPipe;
    private reload: Subject<void>;
    @ViewChild('subjectAssignments') private subjectAssignmentDialog: TemplateRef<any>;
    @ViewChild('newAssignment') private newAssignmentDialog: TemplateRef<any>;
    @ViewChild('newRootNode') private newRootNodeDialog: TemplateRef<any>;
    @ViewChild('deleteResource') private deleteResourceDialog: TemplateRef<any>;

    hierarchy: Hierarchy;
    hierarchyEntity: HierarchyEntity;
    entityAssignable: boolean;
    assignments: AuthorizedObject<PermissionAssignment>[];

    // exported in HTML
    ENTITY_NAME_MAX_LENGTH = ENTITY_NAME_MAX_LENGTH;
    ENTITY_NAME_FORBIDDEN_CHARS = ENTITY_NAME_FORBIDDEN_CHARACTERS.join(', ');
    entityNameError = entityNameError;

    constructor(private readonly hierarchyService: HierarchyService,
                private readonly permissionsService: PermissionsService,
                private readonly i18n: I18NService,
                private readonly dialog: MatDialog,
                private readonly msgBoxService: MsgBoxService,
                private readonly apiService: SmagApiService) {
        this.npp = new EntityNicePathPipe();
        this.reload = new Subject<void>();
        this.hierarchy = null;
        this.hierarchyEntity = null;
        this.entityAssignable = null;
        this.assignments = null;
    }

    ngOnInit() {
        const entityObs = combineLatest([this.hierarchyService.hierarchy, this.hierarchyService.selection]).pipe(
            map(([hierarchy, entity]) => [hierarchy, entity, isEntityAssignable(hierarchy, entity)] as [Hierarchy, HierarchyEntity, boolean]),
            shareReplay(1),
            takeUntil(this.destroyed)
        );
        entityObs.subscribe(([hierarchy, hierarchyEntity, entityAssignable]) => {
            this.hierarchy = hierarchy;
            this.hierarchyEntity = hierarchyEntity;
            this.entityAssignable = entityAssignable;
        });
        combineLatest([entityObs, this.reload]).pipe(
            tap(() => this.assignments = null), // trigger loading spinner
            switchMap(([[hierarchy, hierarchyEntity, entityAssignable]]) => entityAssignable ?
                this.loadAssignments(hierarchy, hierarchyEntity) :
                of(null)),
            takeUntil(this.destroyed)
        ).subscribe(assignments => {
            this.assignments = assignments;
        });
        this.reload.next();
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    isDefined(value: any): boolean {
        return isDefined(value);
    }

    get rootNodeSelected(): boolean {
        return this.hierarchyEntity?.type === EntityType.ROOT_NODE;
    }

    get canDeleteResource(): boolean {
        return can(this.hierarchyEntity.permissions, Permission.DELETE);
    }

    get canCreateRootNode(): boolean {
        return can(this.hierarchy.permissions, Permission.CREATE); // NOTE: root nodes can only be created under "true" root
    }

    onClickCreateRootNode() {
        const nameCtrl = new FormControl('',
            [validateNotBlank, Validators.pattern(ENTITY_NAME_VALIDATION_PATTERN)],
            [this.validateNameUniqueness.bind(this)]);
        this.dialog.open(this.newRootNodeDialog, {
            data: {
                name: nameCtrl,
            },
            minWidth: `${NEW_ROOT_NODE_DIALOG_MIN_WIDTH}px`,
            hasBackdrop: true
        }).afterClosed().subscribe((create: boolean) => {
            if (create) {
                this.createRootNode(nameCtrl.value);
            }
        });
    }

    onClickDeleteResource() {
        this.dialog.open(this.deleteResourceDialog, {
            hasBackdrop: true
        }).afterClosed().subscribe(del => {
            if (del) {
                this.deleteResource();
            }
        });
    }

    onClickNewAssignment() {
        this.dialog.open(this.newAssignmentDialog, {
            hasBackdrop: true,
            data: { // bound to $implicit
                subjectType: PermissionSubjectType.USER, // NOTE: only USER subjects currently supported
                object: {
                    type: PermissionObjectType.ENTITY,
                    id: this.hierarchyEntity ? this.hierarchyEntity.id : this.hierarchy.root,
                    permissionSettings: this.mkPermissionSettings(),
                    hint: this.hierarchyEntity ?
                        this.npp.transform(this.hierarchyEntity.path, false) :
                        this.i18n.translate('resources')
                } as PermissionAssignmentObject,
            }
        }).afterClosed().subscribe((assignment: PermissionAssignment) => {
            if (assignment) {
                this.addAssignment(assignment)
                    .then(() => {
                        this.msgBoxService.info(this.i18n.translate('Permissions added'));
                        this.reload.next(); // trigger permission reloading
                    })
                    .catch(err => {
                        this.msgBoxService.error(this.i18n.translate('Cannot add permissions'), err);
                    });
            }
        });
    }

    onClickSubjectAssignments(subject: AssignmentSubject) {
        this.dialog.open(this.subjectAssignmentDialog, {
            hasBackdrop: true,
            data: { // bound to $implicit
                subjectType: subject.type,
                subjectId: subject.id,
                hint: subject.hint
            }
        });
    }

    private loadAssignments(hierarchy: Hierarchy, entity?: HierarchyEntity): Observable<AuthorizedObject<PermissionAssignment>[]> {
        return this.permissionsService.getPermissionsForObject(PermissionObjectType.ENTITY, entity ? entity.id : hierarchy.root);
    }

    private addAssignment(assignment: PermissionAssignment): Promise<void> {
        return this.permissionsService.assignPermissions(assignment);
    }

    private mkPermissionSettings(): PermissionSettings {
        return {
            mode: PermissionMode.PATTERN,
            allPatterns: this.hierarchyEntity ? allResourcePermissionPatterns(this.hierarchyEntity) : ALL_RESOURCE_PERMISSION_PATTERNS
        } as PatternPermissionSettings;
    }

    private createRootNode(name: string) {
        this.apiService.addEntity(name, null, {type: EntityType.ROOT_NODE} as RootNode)
            .then(idResponse => {
                this.msgBoxService.info(this.i18n.translate('Resource {0} successfully added', name));
                console.debug(`Created root node ${name} (${idResponse.id})`);
            })
            .then(() => this.hierarchyService.reloadHierarchy())
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot add resource {0}', name), err);
                throw err;
            });
    }

    private deleteResource() {
        const name = this.hierarchyEntity.name;
        this.apiService.deleteEntity(this.hierarchyEntity.id)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('Resource {0} deleted', name));
            })
            .then(() => this.hierarchyService.reloadHierarchy())
            .then(() => this.hierarchyService.select(null)) // clear resource selection
            .catch(err => {
                this.msgBoxService.error(this.i18n.translate('Cannot delete resource {0}', name), err);
            });
    }

    private validateNameUniqueness(ctrl: AbstractControl): Observable<ValidationErrors | null> {
        const name = ctrl.value ? ctrl.value.trim() : null;
        if (!name) {
            return of(null);
        }
        return this.hierarchyService.hierarchy.pipe(
            first(),
            map(hierarchy => hierarchy.entities || []),
            map(siblings => !!siblings.find(sibling => sibling.name === name)),
            map(found => found ? {'duplicate': true} : null)
        );
    }
}

function isEntityAssignable(hierarchy: Hierarchy, entity: HierarchyEntity): boolean {
    return entity ?
        can(entity.permissions, Permission.PERMISSIONS) :
        can(hierarchy.permissions, Permission.PERMISSIONS); // hierarchy's root's permissions
}
